import { LitDocEditor, type PageEditor } from '@affine/core/blocksuite/editors';
import { normalizeTextBound } from '@blocksuite/affine/gfx/text';
import { Bound } from '@blocksuite/affine/global/gfx';
import {
  ConnectorElementModel,
  type FrameBlockModel,
} from '@blocksuite/affine/model';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import type { Store } from '@blocksuite/affine/store';
import { useFramework } from '@toeverything/infra';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as Y from 'yjs';

import type { AffineEditorContainer } from './blocksuite-editor';
import * as styles from './object-annotation.css';
import { ObjectAnnotationMetadata } from './object-annotation-metadata';
import {
  AnnotationPicker,
  rememberVocabulary,
} from './object-annotation-picker';
import {
  annotationSpecs,
  configureAnnotationSlashMenu,
} from './object-annotation-specs';
import {
  type AnnotationTarget,
  createAnnotationStore,
  getAnnotation,
} from './object-annotation-store';
import { resolveAnnotationTarget } from './object-annotation-target';
import {
  GlobalAnnotationAI,
  ObjectWorkflow,
} from './object-annotation-workflow';

export function ObjectAnnotationSidebar({
  editor,
  open,
  onOpenChange,
  readonly,
}: {
  editor: AffineEditorContainer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  readonly: boolean;
}) {
  const [target, setTarget] = useState<AnnotationTarget | null>(null);
  const [multiple, setMultiple] = useState(false);
  const panel = useRef<HTMLElement>(null);

  useEffect(() => {
    let cancelled = false;
    let dispose = () => {};
    editor.updateComplete
      .then(() => {
        if (cancelled) return;
        const update = () => {
          const gfx = editor.std?.getOptional(GfxControllerIdentifier);
          const ids = new Set(gfx?.selection?.selectedIds ?? []);
          if (!ids.size) {
            editor.std.selection.value.forEach(sel => {
              if ('blockId' in sel && ['text', 'block'].includes(sel.type))
                ids.add(String(sel.blockId));
            });
          }
          // Focus moving into the sidebar must retain its owner selection.
          if (!ids.size && panel.current?.contains(document.activeElement))
            return;
          setMultiple(ids.size > 1);
          const next =
            ids.size === 1
              ? resolveAnnotationTarget(editor, [...ids][0])
              : null;
          setTarget(previous =>
            previous?.id === next?.id &&
            previous?.data === next?.data &&
            previous?.name === next?.name
              ? previous
              : next
          );
        };
        const subscription =
          editor.std.selection.slots.changed.subscribe(update);
        const blockSubscription = editor.doc.slots.blockUpdated.subscribe(
          event => {
            if (event.type === 'delete') update();
          }
        );
        const gfxSubscription = editor.std
          ?.getOptional(GfxControllerIdentifier)
          ?.selection?.slots.updated.subscribe(update);
        update();
        dispose = () => {
          subscription.unsubscribe();
          blockSubscription.unsubscribe();
          gfxSubscription?.unsubscribe();
        };
      })
      .catch(console.error);
    return () => {
      cancelled = true;
      dispose();
    };
  }, [editor]);

  if (!open)
    return (
      <button
        className={styles.openButton}
        data-testid="object-annotations-open"
        onClick={() => onOpenChange(true)}
      >
        Annotations
      </button>
    );
  return (
    <aside
      ref={panel}
      className={styles.sidebar}
      aria-label="Object annotations"
      data-testid="object-annotations-sidebar"
      onPointerDown={e => e.stopPropagation()}
      onKeyDown={e => {
        // Native BlockSuite keymaps listen on document. Let note-editor keys
        // reach its active dispatcher; metadata controls stay isolated.
        if (
          !(e.target instanceof Element) ||
          !e.target.closest('[data-testid="object-annotation-notes"]')
        )
          e.stopPropagation();
      }}
    >
      <header className={styles.header}>
        <strong>Object annotations</strong>
        <button
          className={styles.iconButton}
          aria-label="Close annotations"
          onClick={() => onOpenChange(false)}
        >
          ×
        </button>
      </header>
      <GlobalAnnotationAI editor={editor} readonly={readonly} />
      {target ? (
        <ObjectNotes
          key={target.id}
          editor={editor}
          parent={editor.doc}
          target={target}
          readonly={readonly}
        />
      ) : (
        <div className={styles.empty}>
          <strong>{multiple ? 'Select one object' : 'Select an object'}</strong>
          <p>
            Choose a rectangle, circle, text block or connector to add labels
            and notes.
          </p>
        </div>
      )}
    </aside>
  );
}

function ObjectNotes({
  editor,
  parent,
  target,
  readonly,
}: {
  editor: AffineEditorContainer;
  parent: Store;
  target: AnnotationTarget;
  readonly: boolean;
}) {
  const [annotation, setAnnotation] = useState<Y.Map<unknown> | null>(null);
  const [labels, setLabels] = useState<[string, string][]>([]);
  const [error, setError] = useState('');
  const [store, setStore] = useState<Store | null>(null);
  const editorRef = useRef<PageEditor>(null);
  const framework = useFramework();
  const specs = useMemo(() => annotationSpecs(framework), [framework]);

  const { id, data } = target;
  useEffect(() => {
    const saved = getAnnotation({ id, data, kind: '', name: '' }, parent);
    setAnnotation(saved);
    if (!saved) return;
    const labelMap = saved.get('labels') as Y.Map<string>;
    const updateLabels = () => setLabels([...labelMap.entries()]);
    updateLabels();
    labelMap.observe(updateLabels);
    const notes = createAnnotationStore(parent, saved);
    notes.store.readonly = readonly || parent.readonly;
    setStore(notes.store);
    return () => {
      labelMap.unobserve(updateLabels);
      notes.dispose();
    };
  }, [parent, id, data, readonly]);

  useEffect(() => {
    let cancelled = false;
    let dispose = () => {};
    if (store && editorRef.current) {
      editorRef.current.updateComplete
        .then(() => {
          if (!cancelled && editorRef.current)
            dispose = configureAnnotationSlashMenu(editorRef.current.std);
        })
        .catch(e => setError(String(e)));
    }
    return () => {
      cancelled = true;
      dispose();
    };
  }, [store]);

  const addLabel = (name: string, color: string) => {
    if (!name || !annotation || readonly || parent.readonly) return;
    const labelMap = annotation.get('labels') as Y.Map<string>;
    const duplicate = [...labelMap.keys()].some(
      key => key.toLocaleLowerCase() === name.toLocaleLowerCase()
    );
    if (duplicate) {
      setError('This label is already attached.');
      return;
    }
    parent.captureSync();
    parent.transact(() => {
      labelMap.set(name, color);
      rememberVocabulary(parent, 'labels', name, color);
    });
    setError('');
  };

  return (
    <div
      className={styles.content}
      data-object-id={target.id}
      data-object-kind={target.kind}
    >
      <div className={styles.objectSummary}>
        <span className={styles.kind}>{target.kind}</span>
        <ObjectText editor={editor} target={target} readonly={readonly} />
      </div>
      {annotation && (
        <ObjectWorkflow
          parent={parent}
          annotation={annotation}
          readonly={readonly}
        />
      )}
      <section className={styles.labels}>
        <div className={styles.sectionTitle}>Labels</div>
        <div className={styles.labelRow}>
          {labels.map(([name, labelColor]) => (
            <span
              className={styles.label}
              key={name}
              style={{ backgroundColor: labelColor }}
              data-testid="object-annotation-label"
            >
              {name}
              {!readonly && (
                <button
                  aria-label={'Remove label ' + name}
                  onClick={() => {
                    parent.captureSync();
                    parent.transact(() =>
                      (
                        annotation?.get('labels') as Y.Map<string> | undefined
                      )?.delete(name)
                    );
                  }}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {!readonly && !parent.readonly && (
            <AnnotationPicker
              parent={parent}
              kind="labels"
              attached={labels.map(([name]) => name)}
              onSelect={addLabel}
            />
          )}
        </div>
      </section>
      {annotation && (
        <ObjectAnnotationMetadata
          parent={parent}
          annotation={annotation}
          readonly={readonly}
        />
      )}
      <div className={styles.notesHeader}>
        <strong>Notes</strong>
        <span>{readonly ? 'Read only' : 'Saved with this object'}</span>
      </div>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      <div
        className={styles.notes + ' mpx-annotation-notes'}
        data-testid="object-annotation-notes"
      >
        {store ? (
          <LitDocEditor
            key={target.id}
            ref={editorRef}
            doc={store}
            specs={specs}
          />
        ) : (
          <p>{readonly ? 'No notes yet.' : 'Loading notes…'}</p>
        )}
      </div>
    </div>
  );
}

function ObjectText({
  editor,
  target,
  readonly,
}: {
  editor: AffineEditorContainer;
  target: AnnotationTarget;
  readonly: boolean;
}) {
  const [text, setText] = useState('');
  useEffect(() => {
    const update = () =>
      setText(
        String(
          (target.kind === 'frame'
            ? (
                editor.doc.getBlock(target.id)?.model as
                  | FrameBlockModel
                  | undefined
              )?.props.title
            : editor.doc.getBlock(target.id)?.model.text) ??
            target.data.get('text') ??
            ''
        )
      );
    update();
    const connector = editor.std
      ?.getOptional(GfxControllerIdentifier)
      ?.getElementById(target.id);
    if (
      !readonly &&
      !editor.doc.readonly &&
      connector instanceof ConnectorElementModel &&
      connector.text?.length &&
      connector.labelDisplay &&
      !connector.labelXYWH
    ) {
      editor.doc.transact(() => updateConnectionLabel(editor, connector));
    }
    target.data.observeDeep(update);
    return () => target.data.unobserveDeep(update);
  }, [editor, readonly, target.data, target.id, target.kind]);
  const block = editor.doc.getBlock(target.id)?.model;
  const element = editor.std
    ?.getOptional(GfxControllerIdentifier)
    ?.getElementById(target.id);
  const editable =
    (target.kind === 'frame' && !!block) ||
    !!block?.text ||
    (!!element && ['shape', 'text', 'connector'].includes(target.kind));
  if (!editable)
    return <strong className={styles.objectName}>{target.name}</strong>;
  return (
    <textarea
      className={styles.objectText}
      aria-label={target.kind === 'frame' ? 'Frame name' : 'Object text'}
      placeholder={
        target.kind === 'frame' ? 'Name this frame…' : 'Add object text…'
      }
      value={text}
      readOnly={readonly || editor.doc.readonly}
      rows={2}
      onChange={event => {
        if (readonly || editor.doc.readonly) return;
        const value = event.target.value;
        editor.doc.captureSync();
        editor.doc.transact(() => {
          const native =
            target.kind === 'frame'
              ? (block as FrameBlockModel | undefined)?.props.title
              : block?.text;
          const surfaceText = target.data.get('text');
          const current = String(native ?? surfaceText ?? '');
          let start = 0;
          while (
            start < current.length &&
            start < value.length &&
            current[start] === value[start]
          )
            start++;
          let end = 0;
          while (
            end < current.length - start &&
            end < value.length - start &&
            current[current.length - 1 - end] === value[value.length - 1 - end]
          )
            end++;
          if (native) {
            native.delete(start, current.length - start - end);
            native.insert(value.slice(start, value.length - end), start);
          } else if (surfaceText instanceof Y.Text) {
            surfaceText.delete(start, current.length - start - end);
            surfaceText.insert(start, value.slice(start, value.length - end));
          } else
            editor.std
              .getOptional(GfxControllerIdentifier)
              ?.updateElement(target.id, { text: new Y.Text(value) });

          if (element instanceof ConnectorElementModel) {
            updateConnectionLabel(editor, element);
          }
        });
      }}
    />
  );
}

function updateConnectionLabel(
  editor: AffineEditorContainer,
  connector: ConnectorElementModel
) {
  const gfx = editor.std?.getOptional(GfxControllerIdentifier);
  if (!gfx) return;
  const text = connector.text;
  if (!text?.length) {
    gfx.updateElement(connector.id, {
      labelDisplay: false,
      labelXYWH: undefined,
    });
    return;
  }
  const options = {
    yText: text,
    ...connector.labelStyle,
    ...connector.labelConstraints,
  };
  let bounds = normalizeTextBound(
    options,
    Bound.fromXYWH(connector.labelXYWH ?? [0, 0, 16, 16])
  );
  // Use native wrapped-text metrics at the final width for long/multiline names.
  bounds = normalizeTextBound(options, bounds, true);
  bounds.w = Math.max(bounds.w, 16);
  bounds.h = Math.max(bounds.h, 16);
  bounds.center = connector.getPointByOffsetDistance(
    connector.labelOffset.distance
  );
  gfx.updateElement(connector.id, {
    labelDisplay: true,
    labelXYWH: bounds.toXYWH(),
  });
}
